import { isNumber } from 'lodash';
import { useContext } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { createDefaultReflection, VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { type IVectorEffectReflection, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { EffectPropsContainer } from '@/components/effect-props-container';
import { EffectPropsGroup } from '@/components/effect-props-group';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';

const FALLBACK_OPACITY = 70;

export const ReflectionInspector: React.FC = () => {
    const { layerId, recordId } = useContext(VectorPanelContext)!;

    const { $t } = useLocaleStore();

    const editor = useEditor();
    const reflection = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const effect = layerData.effect;

        if (!effect || !effect.reflection) return null;

        const reflectionEffect = effect.reflection || [];
        const reflection = reflectionEffect.length > 0 ? reflectionEffect[0] : null;

        return reflection;
    }) as IVectorEffectReflection | null;

    const hidden = reflection ? Boolean(reflection.hidden) : true;
    const size = reflection ? reflection.size : 0;
    const blur = reflection ? reflection.blur : 0;
    const distance = reflection ? reflection.y / 10 : 0;
    const opacity = reflection ? Math.round(reflection.opacity * 100) : FALLBACK_OPACITY;

    const add = () => {
        if (reflection) return;

        const defaultReflection = createDefaultReflection();
        editor.getInstance(VectorLayer).addReflection(layerId, defaultReflection);

        Tracker.addReflection({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const remove = () => {
        if (!reflection) return;
        editor.getInstance(VectorLayer).removeReflection(layerId);

        Tracker.removeReflection({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const setVisibility = (flag: boolean) => {
        if (!reflection) return;
        editor.getInstance(VectorLayer).setReflectionVisibility(layerId, flag);

        Tracker.updateReflectionProps({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const adjustSize = (value: number, options = { writeHistory: true }) => {
        if (!reflection || !isNumber(value)) return;

        editor.getInstance(VectorLayer).adjustReflectionProps(layerId, { size: value }, options);

        if (options.writeHistory) {
            Tracker.updateReflectionProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const adjustBlur = (value: number, options = { writeHistory: true }) => {
        if (!reflection || !isNumber(value)) return;

        editor.getInstance(VectorLayer).adjustReflectionProps(layerId, { blur: value }, options);

        if (options.writeHistory) {
            Tracker.updateReflectionProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const adjustDistance = (value: number, options = { writeHistory: true }) => {
        if (!reflection || !isNumber(value)) return;

        const distance = value * 10;
        editor.getInstance(VectorLayer).adjustReflectionProps(layerId, { y: distance }, options);

        if (options.writeHistory) {
            Tracker.updateReflectionProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const adjustOpacity = (value: number, options = { writeHistory: true }) => {
        if (!reflection || !isNumber(value)) return;

        const opacity = value / 100;
        editor.getInstance(VectorLayer).adjustReflectionProps(layerId, { opacity }, options);

        if (options.writeHistory) {
            Tracker.updateReflectionProps({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    return (
        <EffectPropsContainer
            icon="ReflectionBold"
            title={$t((v) => v.panel.vectorPanel.effect.reflection)}
            showAdd={reflection == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
        >
            {reflection ? (
                <div className="vector-reflection-inspector">
                    <EffectPropsGroup disabled={hidden}>
                        {/* 大小 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.effect.size)} align>
                            <SliderInput
                                showStepBtn
                                value={size}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustSize(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustSize(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 模糊 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.blur)} align>
                            <SliderInput
                                showStepBtn
                                value={blur}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustBlur(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustBlur(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 距离 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.effect.distance)} align>
                            <SliderInput
                                showStepBtn
                                value={distance}
                                min={-100}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustDistance(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustDistance(value, { writeHistory: true })}
                            />
                        </LabelAction>

                        {/* 透明度 */}
                        <LabelAction title={$t((v) => v.panel.vectorPanel.opacity)} align>
                            <SliderInput
                                showStepBtn
                                value={opacity}
                                min={0}
                                max={100}
                                precision={1}
                                type="horizontal"
                                onChange={(value) => adjustOpacity(value, { writeHistory: false })}
                                onAfterChange={(value) => adjustOpacity(value, { writeHistory: true })}
                            />
                        </LabelAction>
                    </EffectPropsGroup>
                </div>
            ) : null}
        </EffectPropsContainer>
    );
};
